import { trackEvent as trackFinEvent } from '@intercom/messenger-js-sdk'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton, ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { AlertCircle, ArrowRight, ArrowUpRight, Check, RefreshCw, ShieldCheck } from 'lucide-react'
import { type ReactNode, useEffect, useRef } from 'react'
import { useStartupContext } from '../../../contexts/startup-guard'
import { trackEvent } from '../../../lib/gtag'
import { openIntercom } from '../../../lib/intercom'
import { CONNECT_LINKEDIN_HELP_URL, LINKEDIN_SAFETY_HELP_URL } from '../config/launch-checklist'
import { useEnableOutreachMutation } from '../hooks/use-enable-outreach-mutation'
import type { OutreachStatusData } from '../types'
import { deriveConnectState, heyReachHrefForStatus } from './connect-linkedin-action'

// Intercom event that triggers the "Connect exit" Fin survey (64681631). Fired
// when a founder dismisses the Connect dialog without having connected. The
// once-per-person frequency lives in Intercom (keyed to identity, so dedup is
// account-wide and cross-device); the localStorage flag below just stops repeat
// fires on the same device if they reopen and close again.
const CONNECT_EXIT_EVENT = 'outreach_connect_exit'
// Namespaced by user id (not just startup id) so a shared browser keeps each
// account's "already fired" flag separate after a re-login.
const connectExitFiredKey = (userId: string, startupId: string) =>
  `sf_connect_exit_fired_${userId}_${startupId}`

// How many of the three numbered steps are already done in each waiting state,
// so the list shows a checkmark instead of the step number. Once the sign-in
// link is generated (`awaiting-invitation`) step 1 is done; once the founder has
// signed in and is connecting LinkedIn (`awaiting-linkedin`) steps 1 and 2 are.
const COMPLETED_STEPS: Partial<Record<ReturnType<typeof deriveConnectState>, number>> = {
  'awaiting-invitation': 1,
  'awaiting-linkedin': 2,
}

// Per-state description under the constant "Connect your LinkedIn" title. The
// explainer panels stay visible through every pre-done state — only the
// description and the CTA change as the polled HeyReach flow advances
// (invitation → LinkedIn sign-in → connected).
const WAITING_DESCRIPTIONS: Record<
  Exclude<ReturnType<typeof deriveConnectState>, 'enable' | 'done' | 'provisioning-failed'>,
  string
> = {
  provisioning: 'Your sign-in link will appear right here in a moment.',
  'awaiting-invitation':
    'Your sign-in link is ready. Open it, create your sign-in (about a minute), then connect your LinkedIn.',
  'awaiting-linkedin':
    'Sign in at outreach.seedforge.com and connect LinkedIn with your email, password and a two-factor code.',
}

// The Connect-LinkedIn launch step. Opens as an explainer BEFORE anything is
// provisioned — what happens and why the account stays safe — and only the
// explicit "Get my sign-in link" CTA kicks off the enable mutation (workspace
// + invitation). The same detailed dialog then tracks the polled outreach
// status through the async HeyReach flow (the CTA swaps to the sign-in /
// connect link), ending with a hand-off to the top-up step.
export function ConnectLinkedInDialog({
  startupId,
  open,
  onOpenChange,
  outreachStatus,
  contactsLabel,
  onConnectedContinue,
}: {
  startupId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  outreachStatus: OutreachStatusData | null
  /** Contact-count display string ("85" / "~85") for copy. */
  contactsLabel: string
  /** Called from the connected stage's CTA — advances to the top-up step. */
  onConnectedContinue: () => void
}) {
  const { user } = useStartupContext()
  const enable = useEnableOutreachMutation(startupId)
  const state = deriveConnectState(outreachStatus)
  const href = heyReachHrefForStatus(outreachStatus)

  const handleEnable = () => {
    if (enable.isPending) return
    enable.mutate(undefined, {
      onSuccess: () => trackEvent('outreach_linkedin_connect_started', { startup_id: startupId }),
    })
  }

  // Auto-kick the provisioning mutation the moment the dialog opens in the
  // `enable` state, so the founder doesn't have to click "Get my sign-in link"
  // themselves — the explainer stays visible and the CTA swaps straight to the
  // "Preparing your sign-in link…" state. Fires once per open: the ref resets
  // when the dialog closes, and `handleEnable` no-ops while a request is in
  // flight. Manual retries (`provisioning-failed`) still use the button.
  const autoEnabledRef = useRef(false)
  useEffect(() => {
    if (!open) {
      autoEnabledRef.current = false
      return
    }
    if (state === 'enable' && !autoEnabledRef.current && !enable.isPending) {
      autoEnabledRef.current = true
      handleEnable()
    }
  }, [open, state, enable.isPending])

  const handleOpenChange = (next: boolean) => {
    // Closing the dialog before connecting === a "Connect exit". Skip when
    // already connected (`done`) — that's a success, not an exit.
    const key = connectExitFiredKey(user.id, startupId)
    if (!next && state !== 'done' && !localStorage.getItem(key)) {
      trackFinEvent(CONNECT_EXIT_EVENT)
      localStorage.setItem(key, '1')
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        {state === 'done' ? (
          <>
            <div className="flex flex-col items-center pt-4 text-center">
              <span className="flex size-13 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10">
                <Check className="size-6 text-emerald-400" strokeWidth={2.5} />
              </span>
              <DialogHeader className="mt-4 items-center">
                <DialogTitle>LinkedIn connected</DialogTitle>
                <DialogDescription className="text-center">
                  Your {contactsLabel} intros are ready to send from your own account, so you're all
                  set to launch.
                </DialogDescription>
              </DialogHeader>
            </div>
            <CtaButton onClick={onConnectedContinue} size="lg" fullWidth arrow>
              Done
            </CtaButton>
            <p className="text-center text-[11.5px] text-muted-foreground/80">
              Disconnect in one click, anytime.
            </p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Connect your LinkedIn</DialogTitle>
              <DialogDescription>
                {state === 'enable'
                  ? `One-time secure connection. Your ${contactsLabel} intros go out from your own account, under your name.`
                  : state === 'provisioning-failed'
                    ? 'Something went wrong while setting up your account. Try again below.'
                    : WAITING_DESCRIPTIONS[state]}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <InfoPanel label="What happens when you click" helpHref={CONNECT_LINKEDIN_HELP_URL}>
                <NumberedStep n={1} done={(COMPLETED_STEPS[state] ?? 0) >= 1}>
                  We prepare your sign-in link for{' '}
                  <strong className="font-semibold text-foreground">outreach.seedforge.com</strong>,
                  our sending platform. It appears right here.
                </NumberedStep>
                <NumberedStep n={2} done={(COMPLETED_STEPS[state] ?? 0) >= 2}>
                  You create your sign-in there. One time, about a minute.
                </NumberedStep>
                <NumberedStep n={3} done={(COMPLETED_STEPS[state] ?? 0) >= 3}>
                  You connect LinkedIn with your email, password and a two-factor code. About 5
                  minutes end to end.
                </NumberedStep>
                <p className="mt-3 border-t border-dashed border-border/60 pt-3 text-[12px] leading-relaxed text-muted-foreground">
                  You'll need LinkedIn two-factor authentication switched on. If it is off, turning
                  it on takes about a minute.
                </p>
                <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
                  <strong className="font-semibold text-foreground">
                    LinkedIn Premium is strongly recommended.
                  </strong>{' '}
                  LinkedIn only delivers your intro note if you have Premium — without it, investors
                  get a blank connection request. Founders with Premium have received about 2× more
                  positive investor replies.
                </p>
              </InfoPanel>

              <InfoPanel
                label="Your account stays safe"
                helpHref={LINKEDIN_SAFETY_HELP_URL}
                icon={ShieldCheck}
              >
                <ul className="space-y-1.5">
                  <SafetyItem>≤20 connection requests a day, under LinkedIn's limit.</SafetyItem>
                  <SafetyItem>You approve every message before it sends.</SafetyItem>
                  <SafetyItem>Two-factor stays in your hands.</SafetyItem>
                  <SafetyItem>Disconnect in one click.</SafetyItem>
                </ul>
              </InfoPanel>
            </div>

            {state === 'enable' ? (
              <CtaButton
                onClick={handleEnable}
                loading={enable.isPending}
                size="lg"
                fullWidth
                arrow
              >
                {enable.isPending ? 'Setting up…' : 'Get my sign-in link'}
              </CtaButton>
            ) : state === 'provisioning' ? (
              <CtaButton loading size="lg" fullWidth>
                Preparing your sign-in link…
              </CtaButton>
            ) : state === 'provisioning-failed' ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-[12.5px] text-destructive">
                  <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    Setup failed. This is usually a temporary issue. Try again and it should work.
                  </span>
                </div>
                <CtaButton
                  onClick={handleEnable}
                  loading={enable.isPending}
                  size="lg"
                  fullWidth
                  icon={<RefreshCw />}
                >
                  {enable.isPending ? 'Retrying…' : 'Try again'}
                </CtaButton>
              </div>
            ) : !href ? (
              // The header promises a ready link, so rendering nothing here
              // leaves the founder staring at instructions with no way to act.
              // This says what actually happened and gives them a way out.
              <div className="rounded-xl border border-border/60 bg-card/40 px-4 py-3.5">
                <p className="text-[13.5px] text-foreground/85">
                  Your sign-in link has not arrived yet. It is usually quick; if it does not show up
                  in a few minutes, message us and a human will finish this with you.
                </p>
                <SecondaryButton className="mt-3" onClick={() => openIntercom()}>
                  Message support
                </SecondaryButton>
              </div>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent(
                    state === 'awaiting-linkedin'
                      ? 'outreach_linkedin_account_link_clicked'
                      : 'outreach_invitation_link_clicked',
                    { startup_id: startupId }
                  )
                }
                className={ctaLinkClasses({ size: 'lg', fullWidth: true })}
              >
                <span>
                  {state === 'awaiting-linkedin' ? 'Connect LinkedIn' : 'Open the sign-in link'}
                </span>
                <ArrowRight />
              </a>
            )}

            {state === 'enable' && (
              <p className="text-center text-[11.5px] text-muted-foreground/80">
                Built by a team that deployed $40M+ across 100+ startups.
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function InfoPanel({
  label,
  helpHref,
  icon: Icon,
  children,
}: {
  label: string
  /** Help Center article for this panel — rendered as a "Learn more" link. */
  helpHref: string
  /** Optional leading icon rendered beside the header label. */
  icon?: typeof ShieldCheck
  children: ReactNode
}) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/40 px-5 py-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="size-4 shrink-0 text-emerald-400" strokeWidth={2} />}
          <div className="text-[12px] font-semibold tracking-tight text-foreground">{label}</div>
        </div>
        <a
          href={helpHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-0.5 text-[11.5px] font-medium text-primary hover:text-primary/80"
        >
          Learn more
          <ArrowUpRight className="size-3" />
        </a>
      </div>
      {children}
    </div>
  )
}

function SafetyItem({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" strokeWidth={2.5} />
      <span className="text-[13px] leading-relaxed text-foreground/85">{children}</span>
    </li>
  )
}

function NumberedStep({ n, done, children }: { n: number; done?: boolean; children: ReactNode }) {
  return (
    <div className="mt-2 flex items-start gap-2.5 first:mt-0">
      {done ? (
        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10">
          <Check className="size-3 text-emerald-400" strokeWidth={3} />
        </span>
      ) : (
        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-border/70 text-[10.5px] font-bold text-muted-foreground">
          {n}
        </span>
      )}
      <p className="text-[13px] leading-relaxed text-foreground/85">{children}</p>
    </div>
  )
}
